/**
 * The shell the two LCFS drill-down drawers share.
 *
 * Both answer the same shape of question — one party, every period — so both
 * want the same frame: a right-hand drawer, an identity header, a strip of
 * lifetime totals, then a period table whose rows expand to the allocation rows
 * behind them. Only the columns differ, and those live in the two callers.
 *
 * Why a drawer and not a page: the reader is working down a roster, and the
 * question is "what about this one" rather than "take me somewhere". Keeping the
 * list behind the panel is the point.
 */

import {
  Alert,
  CloseButton,
  Divider,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertCircle, IconChevronRight } from "@tabler/icons-react";
import { type ReactNode, useRef } from "react";
import { formatApiError } from "../../lib/errors";
import { fmtMmbtu } from "../../lib/mmbtuFormat";

export function HistoryDrawer({
  opened,
  onClose,
  title,
  subtitle,
  isPending,
  error,
  children,
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle: ReactNode;
  isPending: boolean;
  error: unknown;
  children: ReactNode;
}) {
  // Mantine keeps the panel mounted through the close animation, but the id is
  // already gone by then — so the caller's title and subtitle have collapsed to
  // their empty fallbacks, and the header visibly blanks on the way out. Holding
  // the last open values keeps the drawer showing what the reader was looking at
  // until it is actually gone.
  const shown = useRef({ title, subtitle });
  if (opened) shown.current = { title, subtitle };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
      padding="lg"
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={4} mt={4}>
              {shown.current.title}
            </Title>
            {shown.current.subtitle}
          </Stack>
          <CloseButton onClick={onClose} aria-label="Close" />
        </Group>

        <Divider />

        {isPending ? (
          <Skeleton height={360} radius="md" />
        ) : error ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">{formatApiError(error)}</Text>
          </Alert>
        ) : (
          children
        )}
      </Stack>
    </Drawer>
  );
}

/** A labelled lifetime figure in the header strip. */
export function TotalStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="md" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {sub}
      </Text>
    </Stack>
  );
}

/** Right-aligned MMBtu cell. Every figure on these screens is one of these. */
export function Mmbtu({ value, dim }: { value: string; dim?: boolean }) {
  return (
    <Text
      size="sm"
      ff="monospace"
      c={dim ? "dimmed" : undefined}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {fmtMmbtu(value)}
    </Text>
  );
}

/**
 * The period row's expand control.
 *
 * A button rather than a clickable `<tr>`: the row is the disclosure, so it has
 * to be reachable and operable from the keyboard, and a row with an onClick is
 * neither.
 */
export function ExpandCell({
  expanded,
  label,
  onToggle,
  children,
}: {
  expanded: boolean;
  label: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <UnstyledButton onClick={onToggle} aria-expanded={expanded} aria-label={label} w="100%">
      <Group gap={6} wrap="nowrap">
        <IconChevronRight
          size={13}
          style={{
            transform: expanded ? "rotate(90deg)" : undefined,
            transition: "transform 120ms ease",
            flexShrink: 0,
          }}
        />
        {children}
      </Group>
    </UnstyledButton>
  );
}

/** The indented detail rows under an expanded period. */
export function DetailTable({ children }: { children: ReactNode }) {
  return (
    <Table.Tr>
      <Table.Td colSpan={99} p={0}>
        <Table striped={false} verticalSpacing={4} withRowBorders={false}>
          <Table.Tbody>{children}</Table.Tbody>
        </Table>
      </Table.Td>
    </Table.Tr>
  );
}

/** "No rows" inside an expanded period, and inside an empty drawer. */
export function Empty({ children }: { children: ReactNode }) {
  return (
    <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
      <Text size="sm">{children}</Text>
    </Alert>
  );
}
